/**
 * product-premium.js
 * Invi Creation — Premium Product Page
 * Handles: variants, gallery (+ swipe), zoom, accordion, qty, cart AJAX,
 *          modal, share, a ONE-TIME size-info popup before
 *          Add to Cart / Buy Now, AND persisted option selections
 *          (size / length / feeding option) so guest customers who
 *          close and reopen the tab don't silently fall back to
 *          default options.
 *
 * Size popup behavior (updated):
 *  - Shown on the FIRST Add to Cart / Buy Now click on this page view.
 *  - Customer can close it any way they like (X, backdrop, Escape, or
 *    the "Got it, Continue" button) — closing it always lets the
 *    original click go through right after.
 *  - Every click AFTER that — Add to Cart, Buy Now, sticky bar, doesn't
 *    matter how many times — skips the popup completely. No checkbox,
 *    no repeated nagging.
 *
 * Selection persistence (NEW):
 *  - Every time a customer picks a size / length / feeding option, the
 *    choice is saved to localStorage under a key unique to that product
 *    page (location.pathname), with a timestamp.
 *  - On page load, BEFORE any default option is applied, we check for a
 *    saved selection. If one exists and isn't older than 48 hours, we
 *    re-apply it and show a small "we restored your last selection" toast
 *    so it's visible, not silent.
 *  - setDefaultTopLength() (which used to force Top Length to 43 on every
 *    load) now only runs if nothing was restored — it must not override
 *    a returning customer's real choice.
 *  - Once the item is actually added to cart, the saved selection for
 *    that product is cleared, so it doesn't leak into a future visit.
 */

(function () {
  'use strict';

  function refreshCartDrawer(focusEl) {
    const drawer = document.querySelector('cart-drawer');
    const drawerInner = document.getElementById('CartDrawer');
    if (!drawer || !drawerInner) return;

    fetch(window.location.pathname + '?section_id=cart-drawer')
      .then(r => {
        if (!r.ok) throw new Error('section fetch failed: ' + r.status);
        return r.text();
      })
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newInner = doc.getElementById('CartDrawer');

        if (newInner) {
          drawerInner.innerHTML = newInner.innerHTML;
        }
      })
      .catch(err => {
        console.warn('[PPP] Drawer refresh failed, opening drawer anyway:', err);
      })
      .finally(() => {
        if (typeof drawer.open === 'function') {
          drawer.open(focusEl);
        }
      });
  }

  // ── Wait for DOM ─────────────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', init);

  function init() {
    const form = document.getElementById('PppProductForm');
    if (!form) return;

    const variantDataEl = document.getElementById('PppVariantData');
    let variants = [];

    if (variantDataEl) {
      try {
        variants = JSON.parse(variantDataEl.textContent);
      } catch (e) {
        console.warn('[PPP] Could not parse variant data:', e);
      }
    }

    // Build the size-confirmation popup first so every other module can use it.
    const requireSizeConfirmation = setupSizeConfirmGate();
    // Expose it globally so the separate sticky-bar IIFE (below) can reuse
    // the exact same popup / "already seen" flag instead of duplicating logic.
    window.__pppRequireSizeConfirmation = requireSizeConfirmation;
    window.__pppSetupBuyNowGate = setupBuyNowGate;

    // ── SELECTION PERSISTENCE: set up saving + attempt a restore ─────────────
    // Must happen BEFORE setupVariants()/setDefaultTopLength() wire up their
    // own "change" handling and defaults, so a restored selection wins over
    // the hard-coded default.
    const persistence = setupSelectionPersistence(form);
    window.__pppClearSelectionPersistence = persistence.clear;
    const wasRestored = persistence.restore();

    setupVariants(form, variants);
setupSwipeGallery();
setupThumbnailClick();
setupZoom();
setupRelatedZoom();
    setupQuantity(form);
    setupCartForm(form, requireSizeConfirmation);
    setupBuyNowGate(document.getElementById('PppBuyNowWrap'), requireSizeConfirmation);
    setupAccordion();
    setupRelatedShare();
    setupModal();
    setupShare();

    // Start listening for future option changes so they get saved.
    persistence.attachListeners();
    setupSizeChoicePopup(form);

    // ── GLOBAL PREFERRED SIZE ACROSS ALL PRODUCTS ────────────────────────────
    // If customer selected a size on any product, auto-apply it across all products
    const preferredApplied = applyGlobalPreferredSize(form);

    // ── DEFAULT TOP LENGTH TO 43 ──────────────────────────────────────────────
    // Only applies when nothing was restored — a returning customer's real
    // selection must never be silently overwritten by this default.
    if (!wasRestored) {
      setDefaultTopLength(form, variants);
    } else if (!preferredApplied) {
      showRestoreToast();
    }
  }

  // ── GLOBAL PREFERRED SIZE HELPERS ──────────────────────────────────────────
  const GLOBAL_SIZE_KEY = 'invi_customer_preferred_size';

  function getGlobalPreferredSize() {
    try {
      return window.localStorage.getItem(GLOBAL_SIZE_KEY);
    } catch (e) {
      return null;
    }
  }

  function setGlobalPreferredSize(size) {
    if (!size) return;
    try {
      window.localStorage.setItem(GLOBAL_SIZE_KEY, size.trim());
    } catch (e) {}
  }

  function applyGlobalPreferredSize(form) {
    const preferredSize = getGlobalPreferredSize();
    if (!preferredSize) return false;

    const sizeInputs = form.querySelectorAll(
      'input[data-variant-option][data-option-name="size"], input[data-variant-option][name="size"]'
    );
    if (!sizeInputs.length) return false;

    let matchedInput = null;
    sizeInputs.forEach(function (inp) {
      if (inp.value.trim().toLowerCase() === preferredSize.toLowerCase()) {
        matchedInput = inp;
      }
    });

    if (matchedInput && !matchedInput.disabled) {
      sizeInputs.forEach(function (inp) { inp.checked = false; });
      matchedInput.checked = true;

      const label = document.querySelector('[data-selected-value="size"]');
      if (label) label.textContent = matchedInput.value;

      matchedInput.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    return false;
  }

  function setupSizeChoicePopup(form) {
    const sizeInputs = form.querySelectorAll(
      'input[data-variant-option][data-option-name="size"], input[data-variant-option][name="size"]'
    );

    sizeInputs.forEach(function (input) {
      input.addEventListener('click', function () {
        const chosenSize = this.value;
        setGlobalPreferredSize(chosenSize);
      });
    });
  }

  // ── SELECTION PERSISTENCE ────────────────────────────────────────────────────
  function setupSelectionPersistence(form) {
    const STORAGE_PREFIX = 'invi_selection::';
    const EXPIRY_MS = 48 * 60 * 60 * 1000; // 48 hours
    const storageKey = STORAGE_PREFIX + window.location.pathname;

    function readSaved() {
      let raw;
      try {
        raw = window.localStorage.getItem(storageKey);
      } catch (e) {
        return null; // localStorage unavailable (private mode etc.) — fail silently
      }
      if (!raw) return null;

      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        return null;
      }

      if (!parsed || !parsed.values || !parsed.ts) return null;
      if (Date.now() - parsed.ts > EXPIRY_MS) {
        clear();
        return null;
      }
      return parsed.values; // { [inputName]: value }
    }

    function save() {
      const inputs = form.querySelectorAll('[data-variant-option]');
      const values = {};
      inputs.forEach(input => {
        if (input.checked) values[input.name] = input.value;
      });
      if (!Object.keys(values).length) return;

      try {
        window.localStorage.setItem(storageKey, JSON.stringify({
          values: values,
          ts: Date.now()
        }));
      } catch (e) {
        // Storage full or unavailable — not worth surfacing to the customer.
      }
    }

    function clear() {
      try {
        window.localStorage.removeItem(storageKey);
      } catch (e) { /* no-op */ }
    }

    function restore() {
      const saved = readSaved();
      if (!saved) return false;

      const inputs = form.querySelectorAll('[data-variant-option]');
      let appliedAny = false;

      // Uncheck everything first so stale defaults from the theme markup
      // don't stack with the restored values.
      inputs.forEach(input => { input.checked = false; });

      inputs.forEach(input => {
        const savedValue = saved[input.name];
        if (savedValue !== undefined && input.value === savedValue) {
          input.checked = true;
          appliedAny = true;

          const optionName = input.dataset.optionName || input.name;
          const label = document.querySelector('[data-selected-value="' + optionName + '"]');
          if (label) label.textContent = input.value;
        }
      });

      if (appliedAny) {
        // Fire change on the last applied input so price/stock/variant-id
        // and any other "change" listeners set up later still sync correctly.
        // setupVariants() attaches its own listeners after this runs, and it
        // also calls updateUI() once on its own at the end of setup, so this
        // dispatch is a safety net for anything relying on a live event.
        inputs.forEach(input => {
          if (input.checked) {
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      return appliedAny;
    }

    function attachListeners() {
      const inputs = form.querySelectorAll('[data-variant-option]');
      inputs.forEach(input => {
        input.addEventListener('change', function () {
          if (this.checked) save();
        });
      });
    }

    return { save: save, clear: clear, restore: restore, attachListeners: attachListeners };
  }

  function showRestoreToast() {
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
      background: #2f6b3a; color: #fff; padding: 12px 24px;
      border-radius: 8px; font-size: 14px; font-weight: 500;
      z-index: 99999; box-shadow: 0 4px 20px rgba(0,0,0,0.25);
      max-width: 90vw; text-align: center;
    `;
    toast.textContent = "We've restored your last selection ✓";
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3500);
  }

  // ── SET DEFAULT TOP LENGTH TO 43 ─────────────────────────────────────────────
  function setDefaultTopLength(form, variants) {
    const selectedSizeInput = form.querySelector(
      'input[data-variant-option][name="size"]:checked, ' +
      'input[data-variant-option][data-option-name="size"]:checked'
    );
    const currentSize = selectedSizeInput ? selectedSizeInput.value : null;

    let targetLength = '43';
    const topLength43Input = form.querySelector(
      'input[data-variant-option][name="top-length"][value="43"], ' +
      'input[data-variant-option][name="top_length"][value="43"], ' +
      'input[data-variant-option][data-option-name="top-length"][value="43"], ' +
      'input[data-variant-option][data-option-name="top_length"][value="43"]'
    );

    if (topLength43Input && currentSize && Array.isArray(variants) && variants.length > 0) {
      const feedingInput = form.querySelector(
        'input[data-variant-option][name*="feeding"]:checked, ' +
        'input[data-variant-option][data-option-name*="feeding"]:checked'
      );
      const currentFeeding = feedingInput ? feedingInput.value : null;

      const is43Available = variants.some(v =>
        v.options.includes(currentSize) &&
        v.options.includes('43') &&
        (currentFeeding ? v.options.includes(currentFeeding) : true) &&
        v.available
      );

      if (!is43Available) {
        const availableVariantForSize = variants.find(v =>
          v.options.includes(currentSize) &&
          (currentFeeding ? v.options.includes(currentFeeding) : true) &&
          v.available
        ) || variants.find(v => v.options.includes(currentSize) && v.available);

        if (availableVariantForSize) {
          const lengthOpt = availableVariantForSize.options.find(opt =>
            ['38', '39', '40', '41', '42', '43', '44', '45', '46'].includes(opt)
          );
          if (lengthOpt) targetLength = lengthOpt;
        }
      }
    }

    const targetInput = form.querySelector(
      'input[data-variant-option][name*="length"][value="' + targetLength + '"], ' +
      'input[data-variant-option][data-option-name*="length"][value="' + targetLength + '"]'
    ) || topLength43Input;

    if (!targetInput) {
      return;
    }

    const allTopLengthInputs = form.querySelectorAll(
      'input[data-variant-option][name="top-length"], ' +
      'input[data-variant-option][name="top_length"], ' +
      'input[data-variant-option][data-option-name="top-length"], ' +
      'input[data-variant-option][data-option-name="top_length"]'
    );
    allTopLengthInputs.forEach(function (inp) {
      inp.checked = false;
    });

    targetInput.checked = true;

    const optionName = targetInput.dataset.optionName || targetInput.name;
    const label = document.querySelector('[data-selected-value="' + optionName + '"]');
    if (label) label.textContent = targetLength;

    targetInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // ── VARIANT HANDLING ─────────────────────────────────────────────────────────
  function setupVariants(form, variants) {
    const inputs         = form.querySelectorAll('[data-variant-option]');
    const variantIdInput = form.querySelector('[data-variant-id]');
    const priceEl        = document.querySelector('[data-regular-price]');
    const compareEl      = document.querySelector('[data-compare-price]');
    const stockEl        = document.querySelector('.ppp-stock');
    const atcBtn         = form.querySelector('[data-add-to-cart]');
    const atcText        = atcBtn ? atcBtn.querySelector('.ppp-atc-text') : null;

    function getSelectedOptions() {
      const map = {};
      inputs.forEach(input => {
        if (input.checked) map[input.name] = input.value;
      });
      return map;
    }

    function findVariant() {
      const selected = [];
      const groups = form.querySelectorAll('.ppp-option-group');
      if (groups.length > 0) {
        groups.forEach(group => {
          const checked = group.querySelector('input[type="radio"]:checked');
          if (checked) selected.push(checked.value);
        });
      } else {
        inputs.forEach(input => {
          if (input.checked) selected.push(input.value);
        });
      }
      return variants.find(v =>
        v.options.length === selected.length &&
        v.options.every((opt, i) => opt === selected[i])
      );
    }

    function updateOptionAvailability() {
      const selectedSizeInput = form.querySelector(
        'input[data-variant-option][name="size"]:checked, ' +
        'input[data-variant-option][data-option-name="size"]:checked'
      );
      const currentSize = selectedSizeInput ? selectedSizeInput.value : null;

      const optionInputs = form.querySelectorAll('[data-variant-option]');

      optionInputs.forEach(input => {
        const optName = (input.dataset.optionName || input.name || '').toLowerCase();
        const value = input.value;

        let hasStock = false;

        if (optName.includes('size')) {
          // A size is available if ANY variant for this size is available
          hasStock = variants.some(variant =>
            variant.options.includes(value) &&
            variant.available
          );
        } else if (currentSize) {
          // A length or feeding option is available if it exists and is in stock for the selected size
          hasStock = variants.some(variant =>
            variant.options.includes(currentSize) &&
            variant.options.includes(value) &&
            variant.available
          );
        } else {
          hasStock = variants.some(variant =>
            variant.options.includes(value) &&
            variant.available
          );
        }

        const parent = input.closest('.ppp-option-btn');

        if (parent) {
          parent.classList.toggle(
            'ppp-option-btn--disabled',
            !hasStock
          );
        }
      });
    }

    function formatMoney(cents) {
      if (typeof cents !== 'number') return String(cents);
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(cents / 100);
    }

    function updateSelectedValueLabel(input) {
      const optionName = input.dataset.optionName;
      const label = document.querySelector(
        '[data-selected-value="' + optionName + '"]'
      );
      if (label) label.textContent = input.value;
    }

    function updateUI() {
      const variant = findVariant();
      if (!variant) return;

      if (variantIdInput) variantIdInput.value = variant.id;

      if (priceEl) priceEl.textContent = formatMoney(variant.price);

      if (compareEl) {
        const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
        compareEl.style.display = hasCompare ? '' : 'none';
        if (hasCompare) compareEl.textContent = formatMoney(variant.compare_at_price);
      }

      const saveEl = document.querySelector('.ppp-price-save');
      if (saveEl) {
        const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
        saveEl.style.display = hasCompare ? '' : 'none';
        if (hasCompare) {
          saveEl.textContent = 'Save ' + formatMoney(variant.compare_at_price - variant.price);
        }
      }

      if (stockEl) {
        let stockText = '';
        let inStock = variant.available;
        let isLow = false;

        if (!inStock) {
          stockText = 'Out of Stock';
        } else if (
          variant.inventory_management === 'shopify' &&
          variant.inventory_quantity > 0 &&
          variant.inventory_quantity <= 5
        ) {
          isLow = true;
          stockText = '⚡ Only ' + variant.inventory_quantity + ' left in stock!';
        } else {
          stockText = 'In Stock · Ready to Dispatch';
        }

        if (stockEl.dataset.stockText !== stockText) {
          stockEl.dataset.stockText = stockText;

          let dot = stockEl.querySelector('.ppp-stock-dot');
          if (!dot) {
            dot = document.createElement('span');
            dot.className = 'ppp-stock-dot';
            stockEl.appendChild(dot);
          }

          let textNode = stockEl.querySelector('.ppp-stock-text');
          if (!textNode) {
            textNode = document.createElement('span');
            textNode.className = 'ppp-stock-text';
            stockEl.appendChild(textNode);
          }
          textNode.textContent = stockText;

          stockEl.className =
            'ppp-stock ' +
            (!inStock ? 'ppp-stock--out' : (isLow ? 'ppp-stock--low' : 'ppp-stock--in'));
        }
      }

      if (atcBtn) {
        atcBtn.disabled = !variant.available;
        if (atcText) atcText.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      }

      document.dispatchEvent(new CustomEvent('ppp:variantChanged', { detail: { variant: variant } }));
    }

    updateOptionAvailability();

    inputs.forEach(input => {
      input.addEventListener('change', function () {
        updateSelectedValueLabel(this);

        const optName = (this.dataset.optionName || this.name || '').toLowerCase();
        if (optName.includes('size')) {
          const newSize = this.value;
          const currentVariant = findVariant();
          if (!currentVariant || !currentVariant.available) {
            // Find first available variant for this newly selected size
            const availableVariant = variants.find(v => v.options.includes(newSize) && v.available);
            if (availableVariant) {
              availableVariant.options.forEach(val => {
                const targetInp = form.querySelector('input[data-variant-option][value="' + val + '"]');
                if (targetInp && !targetInp.checked) {
                  const grp = targetInp.closest('.ppp-option-group');
                  if (grp) {
                    grp.querySelectorAll('input[type="radio"]').forEach(r => { r.checked = false; });
                    targetInp.checked = true;
                    updateSelectedValueLabel(targetInp);
                  }
                }
              });
            }
          }
        }

        updateOptionAvailability();
        updateUI();
      });
    });

    updateUI();
  }

  // ── GALLERY (thumbnail click) ───────────────────────────────────────────────
  // ── GALLERY: premium mobile swipe ─────────────────────────────────────────
function setupSwipeGallery() {
  const wrap = document.getElementById('PppMainImgWrap');
  const mainImg = document.getElementById('PppMainImage');

  if (!wrap || !mainImg) return;

  const thumbs = Array.from(
    document.querySelectorAll('.ppp-thumb[data-src]')
  );

  if (thumbs.length < 2) return;

  const mobileQuery = window.matchMedia('(max-width: 768px)');

  let startX = 0;
  let startY = 0;
  let dragX = 0;
  let tracking = false;
  let isHorizontal = null;
  let isAnimating = false;
  let nextImg = null;
  let nextIndex = -1;
  let swipeDirection = 0;

  const SWIPE_THRESHOLD = 50;
  const DIRECTION_THRESHOLD = 8;
   
  /*
   * Create a second image that sits beside the current image.
   * This is what makes the swipe look like a real carousel instead
   * of dragging one image away and replacing it.
   */
  function createNextImage(index, direction) {
    const thumb = thumbs[index];
    if (!thumb) return null;

    const img = document.createElement('img');

    img.src = thumb.dataset.src || '';
    if (thumb.dataset.srcset) {
      img.srcset = thumb.dataset.srcset;
    }

    img.alt = mainImg.alt || '';
    img.draggable = false;
    img.setAttribute('aria-hidden', 'true');

    const computed = window.getComputedStyle(mainImg);

    img.style.position = 'absolute';
    img.style.inset = '0';
    img.style.width = computed.width;
    img.style.height = computed.height;
    img.style.maxWidth = computed.maxWidth;
    img.style.maxHeight = computed.maxHeight;
    img.style.objectFit = computed.objectFit;
    img.style.objectPosition = computed.objectPosition;
    img.style.display = computed.display;
    img.style.borderRadius = computed.borderRadius;
    img.style.pointerEvents = 'none';
    img.style.userSelect = 'none';
    img.style.webkitUserDrag = 'none';
    img.style.willChange = 'transform';

    /*
     * Direction:
     *  1  = next image enters from right
     * -1  = previous image enters from left
     */
    img.style.transform =
      'translate3d(' + (direction * wrap.offsetWidth) + 'px, 0, 0)';

    img.style.zIndex = '0';

    wrap.insertBefore(img, mainImg);

    return img;
  }

  function setSlideTransforms(currentX, direction, width) {
    mainImg.style.transform =
      'translate3d(' + currentX + 'px, 0, 0)';

    if (nextImg) {
      nextImg.style.transform =
        'translate3d(' +
        (currentX + direction * width) +
        'px, 0, 0)';
    }
  }

  function cleanupSlide() {
    if (nextImg) {
      nextImg.remove();
      nextImg = null;
    }

    mainImg.style.transition = 'none';
    mainImg.style.transform = 'translate3d(0, 0, 0)';

    dragX = 0;
    nextIndex = -1;
    swipeDirection = 0;
    isAnimating = false;
  }

  function getCurrentIndex() {
    const index = thumbs.findIndex(function (thumb) {
      return thumb.classList.contains('is-active');
    });

    return index === -1 ? 0 : index;
  }

  function getTargetIndex(direction) {
    const current = getCurrentIndex();
    const total = thumbs.length;

    return (
      (current + direction + total) % total
    );
  }

  function updateActiveThumb(index) {
    thumbs.forEach(function (thumb) {
      thumb.classList.remove('is-active');
    });

    if (thumbs[index]) {
      thumbs[index].classList.add('is-active');
    }
  }

  function finishSwipe(direction) {
    if (isAnimating) return;

    isAnimating = true;

    const width = wrap.offsetWidth || 1;

    /*
     * Current image leaves.
     * Next image takes exactly its place.
     */
    setSlideTransforms(
      direction === 1 ? -width : width,
      direction,
      width
    );

    const finish = function () {
      if (!isAnimating) return;

      if (nextImg) {
        /*
         * Change the real image only after the visual transition
         * has completed.
         */
        mainImg.src = nextImg.currentSrc || nextImg.src;

        if (thumbs[nextIndex] && thumbs[nextIndex].dataset.srcset) {
          mainImg.srcset = thumbs[nextIndex].dataset.srcset;
        }
      }

      updateActiveThumb(nextIndex);

      if (nextImg) {
        nextImg.remove();
        nextImg = null;
      }

      mainImg.style.transition = 'none';
      mainImg.style.transform = 'translate3d(0, 0, 0)';

      dragX = 0;
      nextIndex = -1;
      swipeDirection = 0;
      isAnimating = false;
    };

    mainImg.style.transition =
      'transform 320ms cubic-bezier(0.22, 0.61, 0.36, 1)';

    if (nextImg) {
      nextImg.style.transition =
        'transform 320ms cubic-bezier(0.22, 0.61, 0.36, 1)';
    }

    /*
     * transitionend is the primary completion mechanism.
     */
    mainImg.addEventListener(
      'transitionend',
      finish,
      { once: true }
    );

    /*
     * Safety fallback.
     */
    setTimeout(function () {
      finish();
    }, 360);
  }

  wrap.addEventListener(
    'touchstart',
    function (e) {
      if (
        !mobileQuery.matches ||
        e.touches.length !== 1 ||
        isAnimating
      ) {
        return;
      }

      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;

      dragX = 0;
      tracking = true;
      isHorizontal = null;
      swipeDirection = 0;

      mainImg.style.transition = 'none';
    },
    {
      passive: true
    }
  );

  wrap.addEventListener(
    'touchmove',
    function (e) {
      if (
        !mobileQuery.matches ||
        !tracking ||
        isAnimating ||
        e.touches.length !== 1
      ) {
        return;
      }

      const dx =
        e.touches[0].clientX - startX;

      const dy =
        e.touches[0].clientY - startY;

      /*
       * Determine whether the gesture is horizontal.
       * Vertical movement remains normal page scrolling.
       */
      if (
        isHorizontal === null &&
        (
          Math.abs(dx) > DIRECTION_THRESHOLD ||
          Math.abs(dy) > DIRECTION_THRESHOLD
        )
      ) {
        isHorizontal =
          Math.abs(dx) > Math.abs(dy);
      }

      if (!isHorizontal) return;

      e.preventDefault();

      const width = wrap.offsetWidth || 1;

      swipeDirection = dx < 0 ? 1 : -1;

      /*
       * Create the neighbouring image as soon as the user
       * commits to a horizontal gesture.
       */
      if (!nextImg) {
        nextIndex = getTargetIndex(swipeDirection);

        nextImg = createNextImage(
          nextIndex,
          swipeDirection
        );

        if (!nextImg) return;

        /*
         * Disable transitions while following the finger.
         */
        mainImg.style.transition = 'none';
        nextImg.style.transition = 'none';
      }

      /*
       * A little resistance near the edges gives the gesture
       * a more controlled, premium feel.
       */
      const resistance =
        Math.abs(dx) > width * 0.72
          ? 0.92
          : 1;

      dragX = dx * resistance;

      setSlideTransforms(
        dragX,
        swipeDirection,
        width
      );
    },
    {
      passive: false
    }
  );

  wrap.addEventListener(
    'touchend',
    function () {
      if (
        !tracking ||
        isAnimating
      ) {
        return;
      }

      tracking = false;

      /*
       * Not a horizontal gesture.
       */
      if (!isHorizontal) {
        return;
      }

      /*
       * User didn't swipe far enough.
       * Snap both images smoothly back into place.
       */
      if (
        Math.abs(dragX) < SWIPE_THRESHOLD ||
        !nextImg
      ) {
        const width = wrap.offsetWidth || 1;

        mainImg.style.transition =
          'transform 260ms cubic-bezier(0.22, 0.61, 0.36, 1)';

        nextImg.style.transition =
          'transform 260ms cubic-bezier(0.22, 0.61, 0.36, 1)';

        setSlideTransforms(
          0,
          swipeDirection,
          width
        );

        setTimeout(function () {
          cleanupSlide();
        }, 280);

        return;
      }

      /*
       * Real swipe.
       */
      finishSwipe(swipeDirection);
    },
    {
      passive: true
    }
  );

  wrap.addEventListener(
    'touchcancel',
    function () {
      if (!tracking) return;

      tracking = false;

      if (
        nextImg &&
        !isAnimating
      ) {
        const width = wrap.offsetWidth || 1;

        mainImg.style.transition =
          'transform 260ms cubic-bezier(0.22, 0.61, 0.36, 1)';

        nextImg.style.transition =
          'transform 260ms cubic-bezier(0.22, 0.61, 0.36, 1)';

        setSlideTransforms(
          0,
          swipeDirection,
          width
        );

        setTimeout(function () {
          cleanupSlide();
        }, 280);
      }
    },
    {
      passive: true
    }
  );
}
  function setupThumbnailClick() {
    const mainImg = document.getElementById('PppMainImage');
    const thumbs = Array.from(document.querySelectorAll('.ppp-thumb[data-src]'));
    if (!mainImg || thumbs.length < 1) return;

    // Preload all thumbnail large images in the background so there's zero network delay when clicked
    thumbs.forEach(function (thumb) {
      const src = thumb.dataset.src;
      if (src) {
        const preloader = new Image();
        preloader.src = src;
      }
    });

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function (e) {
        e.preventDefault();
        const src = this.dataset.src;
        const srcset = this.dataset.srcset;
        if (!src) return;

        thumbs.forEach(function (t) { t.classList.remove('is-active'); });
        this.classList.add('is-active');

        mainImg.style.transition = 'opacity 0.15s ease';
        mainImg.style.opacity = '0.85';
        mainImg.src = src;
        if (srcset) mainImg.srcset = srcset;
        requestAnimationFrame(function () {
          mainImg.style.opacity = '1';
        });
      });
    });
  }
  // ── ZOOM MODAL ────────────────────────────────────────────────────────────────
  // ── ZOOM MODAL ─────────────────────────────────────────────────────────────
function setupZoom() {
  const zoomBtn = document.querySelector('[data-zoom]');
  const mainImg = document.getElementById('PppMainImage');
  const overlay = document.getElementById('ImageZoomModal');
  const stage = document.getElementById('PppZoomStage');
  const zoomImg = document.getElementById('PppZoomImage');
  const closeBtn = document.querySelector('[data-zoom-close]');
  const zoomHint = document.getElementById('PppZoomHint');

  if (!overlay || !zoomImg || !stage) return;

  if (overlay.parentElement !== document.body) {
    document.body.appendChild(overlay);
  }

  overlay.style.touchAction = 'none';
  stage.style.touchAction = 'none';
  zoomImg.style.touchAction = 'none';

  let scale = 1;
  let offsetX = 0;
  let offsetY = 0;

  let isDragging = false;
  let dragStartX = 0;
  let dragStartY = 0;

  let pinchStartDist = 0;
  let pinchStartScale = 1;
  let pinchStartOffsetX = 0;
  let pinchStartOffsetY = 0;

  let pinchStartMidX = 0;
  let pinchStartMidY = 0;
  let pinchLocalX = 0;
  let pinchLocalY = 0;

  const MAX_SCALE = 4;
  const CLICK_ZOOM = 2.2;

  function getStageRect() {
    return stage.getBoundingClientRect();
  }

  function getBaseImageSize() {
    /*
     * At scale 1 the image is already centered by flexbox.
     * getBoundingClientRect() therefore gives us the actual
     * displayed dimensions.
     */
    const rect = zoomImg.getBoundingClientRect();

    return {
      width: rect.width,
      height: rect.height
    };
  }

  function clampOffsets(x, y, targetScale) {
    const stageRect = getStageRect();
    const imageSize = getBaseImageSize();

    /*
     * Maximum amount the scaled image can move while still
     * covering the stage.
     */
    const scaledWidth =
      imageSize.width * targetScale;

    const scaledHeight =
      imageSize.height * targetScale;

    const maxX =
      Math.max(
        0,
        (scaledWidth - stageRect.width) / 2
      );

    const maxY =
      Math.max(
        0,
        (scaledHeight - stageRect.height) / 2
      );

    return {
      x: Math.min(
        Math.max(x, -maxX),
        maxX
      ),
      y: Math.min(
        Math.max(y, -maxY),
        maxY
      )
    };
  }

  function applyTransform(animated) {
    zoomImg.style.transition = animated
      ? 'transform 280ms cubic-bezier(0.22, 0.61, 0.36, 1)'
      : 'none';

    zoomImg.style.transform =
      'translate3d(' +
      offsetX +
      'px, ' +
      offsetY +
      'px, 0) scale(' +
      scale +
      ')';
  }

  function resetZoomState(animated) {
    scale = 1;
    offsetX = 0;
    offsetY = 0;

    zoomImg.style.transformOrigin = 'center center';
    zoomImg.classList.remove('is-zoomed');
    if (zoomHint) zoomHint.textContent = 'Click image to zoom in • Drag to pan • Click ✕ to close';

    applyTransform(animated);
  }

  /*
   * Zoom toward the exact point where the customer clicked.
   * Unlike the old implementation, this uses the CENTER of
   * the stage as the transform coordinate system.
   */
  function zoomToPoint(screenX, screenY, newScale) {
    const stageRect = getStageRect();

    const pointX =
      screenX -
      (stageRect.left + stageRect.width / 2);

    const pointY =
      screenY -
      (stageRect.top + stageRect.height / 2);

    /*
     * Preserve the image point underneath the cursor.
     */
    const imagePointX =
      (pointX - offsetX) / scale;

    const imagePointY =
      (pointY - offsetY) / scale;

    let newOffsetX =
      pointX -
      imagePointX * newScale;

    let newOffsetY =
      pointY -
      imagePointY * newScale;

    const clamped =
      clampOffsets(
        newOffsetX,
        newOffsetY,
        newScale
      );

    scale = newScale;
    offsetX = clamped.x;
    offsetY = clamped.y;

    zoomImg.classList.add('is-zoomed');
    if (zoomHint) zoomHint.textContent = 'Drag to pan • Click image to zoom out • Click ✕ to close';

    applyTransform(true);
  }

  function openZoom() {
    if (!mainImg) return;

    if (overlay.parentElement !== document.body) {
      document.body.appendChild(overlay);
    }

    zoomImg.src =
      mainImg.currentSrc ||
      mainImg.src;

    if (mainImg.srcset) {
      zoomImg.srcset = mainImg.srcset;
    }

    /*
     * Wait for the image to load before measuring it.
     */
    overlay.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';

    resetZoomState(false);

    if (zoomImg.complete) {
      resetZoomState(false);
    } else {
      zoomImg.addEventListener(
        'load',
        function () {
          resetZoomState(false);
        },
        { once: true }
      );
    }
  }

  function closeZoom() {
    overlay.setAttribute('hidden', '');
    document.body.style.overflow = '';

    isDragging = false;
    pinchStartDist = 0;

    resetZoomState(false);
  }

  if (zoomBtn) {
    zoomBtn.addEventListener(
      'click',
      function (e) {
        e.preventDefault();
        e.stopPropagation();
        openZoom();
      }
    );
  }

  if (mainImg) {
    mainImg.style.cursor = 'zoom-in';

    mainImg.addEventListener(
      'click',
      function () {
        openZoom();
      }
    );
  }

  if (closeBtn) {
    closeBtn.addEventListener(
      'click',
      closeZoom
    );
  }

  overlay.addEventListener(
    'click',
    function (e) {
      if (e.target === overlay) {
        closeZoom();
      }
    }
  );

  document.addEventListener(
    'keydown',
    function (e) {
      if (
        e.key === 'Escape' &&
        !overlay.hasAttribute('hidden')
      ) {
        closeZoom();
      }
    }
  );

  /*
   * DESKTOP CLICK TO ZOOM
   */
  zoomImg.addEventListener(
    'click',
    function (e) {
      e.stopPropagation();

      if (scale === 1) {
        zoomToPoint(
          e.clientX,
          e.clientY,
          CLICK_ZOOM
        );
      } else {
        resetZoomState(true);
      }
    }
  );

  /*
   * DESKTOP MOUSE DRAG
   */
  zoomImg.addEventListener(
    'mousedown',
    function (e) {
      if (scale <= 1) return;

      e.preventDefault();

      isDragging = true;

      dragStartX =
        e.clientX - offsetX;

      dragStartY =
        e.clientY - offsetY;

      zoomImg.style.cursor =
        'grabbing';
    }
  );

  window.addEventListener(
    'mousemove',
    function (e) {
      if (!isDragging) return;

      let newX =
        e.clientX - dragStartX;

      let newY =
        e.clientY - dragStartY;

      const clamped =
        clampOffsets(
          newX,
          newY,
          scale
        );

      offsetX = clamped.x;
      offsetY = clamped.y;

      applyTransform(false);
    }
  );

  window.addEventListener(
    'mouseup',
    function () {
      if (!isDragging) return;

      isDragging = false;

      if (scale > 1) {
        zoomImg.style.cursor = 'grab';
      }
    }
  );

  /*
   * TOUCH HELPERS
   */
  function getTouchDist(t0, t1) {
    const dx =
      t1.clientX - t0.clientX;

    const dy =
      t1.clientY - t0.clientY;

    return Math.sqrt(
      dx * dx + dy * dy
    );
  }

  function getTouchMid(t0, t1) {
    return {
      x:
        (t0.clientX + t1.clientX) / 2,

      y:
        (t0.clientY + t1.clientY) / 2
    };
  }

  /*
   * MOBILE PINCH + PAN
   */
  overlay.addEventListener(
    'touchstart',
    function (e) {
      if (e.touches.length === 2) {
        const t0 = e.touches[0];
        const t1 = e.touches[1];

        pinchStartDist =
          getTouchDist(t0, t1);

        pinchStartScale = scale;

        pinchStartOffsetX =
          offsetX;

        pinchStartOffsetY =
          offsetY;

        const mid =
          getTouchMid(t0, t1);

        pinchStartMidX = mid.x;
        pinchStartMidY = mid.y;

        const stageRect =
          getStageRect();

        const pointX =
          pinchStartMidX -
          (stageRect.left +
            stageRect.width / 2);

        const pointY =
          pinchStartMidY -
          (stageRect.top +
            stageRect.height / 2);

        pinchLocalX =
          (pointX -
            pinchStartOffsetX) /
          pinchStartScale;

        pinchLocalY =
          (pointY -
            pinchStartOffsetY) /
          pinchStartScale;

        return;
      }

      if (
        scale <= 1 ||
        e.touches.length !== 1
      ) {
        return;
      }

      dragStartX =
        e.touches[0].clientX -
        offsetX;

      dragStartY =
        e.touches[0].clientY -
        offsetY;
    },
    {
      passive: true
    }
  );

  overlay.addEventListener(
    'touchmove',
    function (e) {
      /*
       * PINCH
       */
      if (e.touches.length === 2) {
        e.preventDefault();

        const newDist =
          getTouchDist(
            e.touches[0],
            e.touches[1]
          );

        if (pinchStartDist <= 0) return;

        const rawScale =
          pinchStartScale *
          (newDist / pinchStartDist);

        const newScale =
          Math.min(
            Math.max(rawScale, 1),
            MAX_SCALE
          );

        const mid =
          getTouchMid(
            e.touches[0],
            e.touches[1]
          );

        const stageRect =
          getStageRect();

        const pointX =
          mid.x -
          (stageRect.left +
            stageRect.width / 2);

        const pointY =
          mid.y -
          (stageRect.top +
            stageRect.height / 2);

        let newOffsetX =
          pointX -
          pinchLocalX * newScale;

        let newOffsetY =
          pointY -
          pinchLocalY * newScale;

        const clamped =
          clampOffsets(
            newOffsetX,
            newOffsetY,
            newScale
          );

        scale = newScale;
        offsetX = clamped.x;
        offsetY = clamped.y;

        zoomImg.classList.toggle(
          'is-zoomed',
          scale > 1
        );

        applyTransform(false);

        return;
      }

      /*
       * ONE FINGER PAN
       */
      if (
        scale <= 1 ||
        e.touches.length !== 1
      ) {
        return;
      }

      e.preventDefault();

      let newX =
        e.touches[0].clientX -
        dragStartX;

      let newY =
        e.touches[0].clientY -
        dragStartY;

      const clamped =
        clampOffsets(
          newX,
          newY,
          scale
        );

      offsetX = clamped.x;
      offsetY = clamped.y;

      applyTransform(false);
    },
    {
      passive: false
    }
  );

  overlay.addEventListener(
    'touchend',
    function (e) {
      if (e.touches.length < 2) {
        pinchStartDist = 0;
      }
    },
    {
      passive: true
    }
  );

  overlay.addEventListener(
    'touchcancel',
    function () {
      pinchStartDist = 0;
    },
    {
      passive: true
    }
  );
}

// ── ZOOM: related-product cards

 
  // ── ZOOM: related-product cards (reuses the main #ImageZoomModal) ───────────
  function setupRelatedZoom() {
    const btns    = document.querySelectorAll('[data-related-zoom]');
    const overlay = document.getElementById('ImageZoomModal');
    const zoomImg = document.getElementById('PppZoomImage');
    if (!btns.length || !overlay || !zoomImg) return;

    if (overlay.parentElement !== document.body) {
      document.body.appendChild(overlay);
    }

    btns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        const src = this.dataset.zoomSrc;
        if (!src) return;

        zoomImg.src = src;
        zoomImg.style.transform = 'translate(0px, 0px) scale(1)';
        zoomImg.classList.remove('is-zoomed');
        overlay.removeAttribute('hidden');
        document.body.style.overflow = 'hidden';
      });
    });
  }

  // ── QUANTITY ─────────────────────────────────────────────────────────────────
  function setupQuantity(form) {
    const input    = form.querySelector('.ppp-qty-input');
    const minusBtn = form.querySelector('.ppp-qty-btn--minus');
    const plusBtn  = form.querySelector('.ppp-qty-btn--plus');

    if (!input || !minusBtn || !plusBtn) return;

    minusBtn.addEventListener('click', () => {
      const val = parseInt(input.value, 10);
      if (val > 1) input.value = val - 1;
    });

    plusBtn.addEventListener('click', () => {
      const val = parseInt(input.value, 10);
      input.value = val + 1;
    });
  }

  // ── SIZE CONFIRMATION POPUP (ONE-TIME, NON-BLOCKING) ─────────────────────────
  // Builds (once) the popup that shows the size chart before Add to Cart /
  // Buy Now. The table is cloned from the existing #SizeGuideModal so
  // there's only ONE place (the SizeGuideModal block in the .liquid file)
  // to maintain the actual size numbers — this popup just reuses it.
  //
  // KEY RULE: it only ever shows ONCE per page view. The first time
  // requireSizeConfirmation() is called, it opens the popup, pauses that
  // one click, and — however the shopper closes it (X, backdrop, Escape,
  // or the "Got it, Continue" button) — immediately fires the original
  // action and flips an internal flag. Every call after that skips the
  // popup entirely and runs the action straight away.
  function setupSizeConfirmGate() {
    return function requireSizeConfirmation(onConfirmed) {
      if (typeof onConfirmed === 'function') {
        onConfirmed();
      }
    };
  }

  // ── BUY NOW GATE ──────────────────────────────────────────────────────────────
  // Shopify's dynamic checkout button ({{ form | payment_button }}) attaches
  // its own click handler once its script loads, and clicking it normally
  // redirects straight to checkout — bypassing any of our own form submit
  // listeners. To gate it we listen on the *wrapping container* in the
  // capture phase, which always fires before the button's own listener,
  // intercept the click, show the size popup (only if not shown yet), and
  // replay the click (letting Shopify's handler run) once it's resolved.
  function setupBuyNowGate(containerEl, requireSizeConfirmation) {
    if (!containerEl || !requireSizeConfirmation) return;

    let allowNextClick = false;

    containerEl.addEventListener('click', function (e) {
      if (allowNextClick) {
        allowNextClick = false;
        return; // let the confirmed click through to Shopify's handler
      }

      const target = e.target.closest('button, input[type="submit"], a');
      if (!target || !containerEl.contains(target)) return;

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation();
      }

      requireSizeConfirmation(function () {
        allowNextClick = true;
        if (window.__pppClearSelectionPersistence) window.__pppClearSelectionPersistence();
        target.click();
      });
    }, true); // capture: true — fires before Shopify's own listener
  }

  // ── CART (AJAX) ───────────────────────────────────────────────────────────────
  function setupCartForm(form, requireSizeConfirmation) {
    let isSubmitting = false;

    function performAddToCart() {
      if (isSubmitting) return;
      isSubmitting = true;

      const atcBtn  = form.querySelector('[data-add-to-cart]');
      const atcText = atcBtn ? atcBtn.querySelector('.ppp-atc-text') : null;
      const origText = atcText ? atcText.textContent : 'Add to Cart';

      if (atcBtn) {
        atcBtn.disabled = true;
        if (atcText) atcText.textContent = 'Adding…';
      }

      fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form)
      })
        .then(r => r.json())
        .then(data => {
          if (data.id) {
            if (atcText) atcText.textContent = '✓ Added!';

            if (window.__pppClearSelectionPersistence) window.__pppClearSelectionPersistence();

            refreshCartDrawer(atcBtn);

            fetch('/cart.js')
              .then(r => r.json())
              .then(cart => {
                document.querySelectorAll('.cart-count-bubble').forEach(el => {
                  el.textContent = cart.item_count;
                });
                document.dispatchEvent(new CustomEvent('cart:refresh'));
              });

            setTimeout(() => {
              if (atcBtn) atcBtn.disabled = false;
              if (atcText) atcText.textContent = origText;
              isSubmitting = false;
            }, 2200);
          } else {
            const errorMsg = data.description || data.message || data.errors || 'Could not add item to cart. Please try again.';
            showCartError(errorMsg);
            resetAtcBtn(atcBtn, atcText, origText);
            isSubmitting = false;
          }
        })
        .catch(err => {
          console.error('[PPP] Cart error:', err);
          resetAtcBtn(atcBtn, atcText, origText);
          isSubmitting = false;
        });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (isSubmitting) return;

      if (requireSizeConfirmation) {
        requireSizeConfirmation(performAddToCart);
      } else {
        performAddToCart();
      }
    });
  }

  function resetAtcBtn(btn, textEl, text) {
    if (btn) btn.disabled = false;
    if (textEl) textEl.textContent = text;
  }

  function showCartError(msg) {
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
      background: #9B2626; color: #fff; padding: 12px 24px;
      border-radius: 8px; font-size: 14px; font-weight: 500;
      z-index: 99999; box-shadow: 0 4px 20px rgba(0,0,0,0.25);
    `;
    toast.textContent = msg || 'Could not add to cart. Please try again.';
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3500);
  }

  // ── ACCORDION ─────────────────────────────────────────────────────────────────
  function setupAccordion() {
    const items = document.querySelectorAll('[data-accordion]');

    items.forEach(item => {
      const trigger = item.querySelector('.ppp-accordion-trigger');
      if (!trigger) return;

      trigger.addEventListener('click', function () {
        const isOpen = item.classList.contains('is-open');

        items.forEach(i => {
          i.classList.remove('is-open');
          const t = i.querySelector('.ppp-accordion-trigger');
          if (t) t.setAttribute('aria-expanded', 'false');
        });

        if (!isOpen) {
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  // ── MODAL ─────────────────────────────────────────────────────────────────────
  function setupModal() {
    document.querySelectorAll('[data-modal]').forEach(btn => {
      btn.addEventListener('click', function () {
        const id = this.dataset.modal;
        const modal = document.getElementById(id);
        if (modal) openModal(modal);
      });
    });

    document.querySelectorAll('[data-modal-close]').forEach(btn => {
      btn.addEventListener('click', function () {
        const modal = this.closest('.ppp-modal-overlay');
        if (modal) closeModal(modal);
      });
    });

    document.querySelectorAll('.ppp-modal-overlay').forEach(overlay => {
      overlay.addEventListener('click', function (e) {
        if (e.target === this) closeModal(this);
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        document.querySelectorAll('.ppp-modal-overlay:not([hidden])').forEach(closeModal);
      }
    });
  }

  function openModal(modal) {
    modal.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    setTimeout(() => {
      const focusable = modal.querySelector('button, [tabindex]:not([tabindex="-1"])');
      if (focusable) focusable.focus();
    }, 50);
  }

  function closeModal(modal) {
    modal.setAttribute('hidden', '');
    document.body.style.overflow = '';
    modal.dispatchEvent(new CustomEvent('ppp:modalclosed'));
  }

  // ── SHARE / COPY LINK ─────────────────────────────────────────────────────────
  function setupShare() {
    const shareBtns = document.querySelectorAll('[data-share]');
    if (shareBtns.length === 0) return;

    shareBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        const isIconOnly = !this.textContent.trim();
        const orig = this.textContent;

        if (navigator.share) {
          navigator.share({
            title: document.title,
            url: window.location.href
          }).catch(() => {});
          return;
        }

        if (navigator.clipboard) {
          navigator.clipboard.writeText(window.location.href).then(() => {
            if (isIconOnly) {
              this.classList.add('is-copied');
              setTimeout(() => this.classList.remove('is-copied'), 2000);
            } else {
              this.textContent = '✓ Copied!';
              setTimeout(() => { this.textContent = orig; }, 2000);
            }
          });
        }
      });
    });
  }

  // ── SHARE: RELATED PRODUCT CARDS ────────────────────────────────────────────
  function setupRelatedShare() {
    const btns = document.querySelectorAll('[data-share-product]');
    if (btns.length === 0) return;

    btns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        const url = this.dataset.url;
        if (!url) return;

        if (navigator.share) {
          navigator.share({ url: url }).catch(() => {});
          return;
        }

        if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(() => {
            this.classList.add('is-copied');
            setTimeout(() => this.classList.remove('is-copied'), 2000);
          });
        }
      });
    });
  }

})();
/* ============================================================
   MOBILE STICKY BAR
   ============================================================ */
(function () {
  'use strict';

  function refreshCartDrawerSticky(focusEl) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    fetch(window.location.pathname + '?section_id=cart-drawer')
      .then(r => {
        if (!r.ok) throw new Error('section fetch failed: ' + r.status);
        return r.text();
      })
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newDrawer = doc.querySelector('cart-drawer');

        if (newDrawer) {
          drawer.innerHTML = newDrawer.innerHTML;
        }
      })
      .catch(err => {
        console.warn('[StickyBar] Drawer refresh failed, opening drawer anyway:', err);
      })
      .finally(() => {
        if (typeof drawer.open === 'function') {
          drawer.open(focusEl);
        }
      });
  }

  function initStickyBar() {
    var stickyBar    = document.getElementById('PppStickyBar');
    var stickyAtcBtn = document.getElementById('PppStickyAtcBtn');
    var stickyBuyBtn = document.getElementById('PppStickyBuyBtn');
    var stickyVarId  = document.getElementById('PppStickyVariantId');
    var mainForm     = document.getElementById('PppProductForm');

    if (!stickyBar || !stickyAtcBtn || !mainForm) {
      console.warn('[StickyBar] Missing elements:', {
        stickyBar: !!stickyBar,
        stickyAtcBtn: !!stickyAtcBtn,
        mainForm: !!mainForm
      });
      return;
    }

    var stickyIsSubmitting = false;

    function getSelectedVariantId() {
      var variantId = stickyVarId && stickyVarId.value
        ? parseInt(stickyVarId.value, 10)
        : null;

      if (!variantId && mainForm) {
        var mainVarInput = mainForm.querySelector('[data-variant-id]');
        variantId = mainVarInput ? parseInt(mainVarInput.value, 10) : null;
      }

      if (!variantId) {
        var checkedRadio = document.querySelector('input[name="id"]:checked') || document.querySelector('input[name="id"]');
        if (checkedRadio) variantId = parseInt(checkedRadio.value, 10);
      }

      return variantId;
    }

    function getSelectedQuantity() {
      var qtyInput = mainForm ? mainForm.querySelector('[name="quantity"]') : null;
      return qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
    }

    function performStickyAddToCart() {
      if (stickyIsSubmitting) return;
      stickyIsSubmitting = true;

      var variantId = getSelectedVariantId();
      if (!variantId) {
        console.warn('[StickyBar] No variant ID found');
        stickyIsSubmitting = false;
        return;
      }

      var quantity = getSelectedQuantity();

      stickyAtcBtn.disabled    = true;
      stickyAtcBtn.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: variantId, quantity: quantity })
      })
      .then(function (res) {
        if (!res.ok) throw new Error(res.status);
        return res.json();
      })
      .then(function () {
        stickyAtcBtn.textContent = '✓ Added!';

        if (window.__pppClearSelectionPersistence) window.__pppClearSelectionPersistence();

        refreshCartDrawerSticky(stickyAtcBtn);

        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            document.querySelectorAll('.cart-count-bubble').forEach(function (el) {
              el.textContent = cart.item_count;
            });
          });

        setTimeout(function () {
          stickyAtcBtn.textContent = 'Add to Cart';
          stickyAtcBtn.disabled    = false;
          stickyIsSubmitting = false;
        }, 2000);
      })
      .catch(function (err) {
        console.error('[StickyBar] Cart error:', err);
        stickyAtcBtn.textContent = 'Try Again';
        stickyAtcBtn.disabled    = false;
        stickyIsSubmitting = false;
      });
    }

    function performStickyBuyNow() {
      if (stickyIsSubmitting) return;
      stickyIsSubmitting = true;

      var variantId = getSelectedVariantId();
      if (!variantId) {
        console.warn('[StickyBar] No variant ID found for Buy Now');
        stickyIsSubmitting = false;
        return;
      }

      var quantity = getSelectedQuantity();

      if (stickyBuyBtn) {
        stickyBuyBtn.disabled = true;
        stickyBuyBtn.textContent = 'Processing…';
      }

      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: variantId, quantity: quantity })
      })
      .then(function (res) {
        if (!res.ok) throw new Error(res.status);
        return res.json();
      })
      .then(function () {
        if (window.__pppClearSelectionPersistence) window.__pppClearSelectionPersistence();
        window.location.href = '/checkout';
      })
      .catch(function (err) {
        console.warn('[StickyBar] Buy Now direct checkout fallback:', err);
        window.location.href = '/cart/' + variantId + ':' + quantity;
      });
    }

    /* ── ADD TO CART click (popup shown only if not seen yet) ── */
    stickyAtcBtn.addEventListener('click', function () {
      if (stickyIsSubmitting) return;

      if (window.__pppRequireSizeConfirmation) {
        window.__pppRequireSizeConfirmation(performStickyAddToCart);
      } else {
        performStickyAddToCart();
      }
    });

    /* ── BUY NOW click (popup shown only if not seen yet) ── */
    if (stickyBuyBtn) {
      stickyBuyBtn.addEventListener('click', function () {
        if (stickyIsSubmitting) return;

        if (window.__pppRequireSizeConfirmation) {
          window.__pppRequireSizeConfirmation(performStickyBuyNow);
        } else {
          performStickyBuyNow();
        }
      });
    }

    /* ── SYNC variant on selection change ── */
    document.addEventListener('ppp:variantChanged', function (e) {
      if (!e.detail || !e.detail.variant) return;
      var v = e.detail.variant;

      if (stickyVarId) stickyVarId.value = v.id;

      stickyAtcBtn.disabled    = !v.available;
      stickyAtcBtn.textContent = v.available ? 'Add to Cart' : 'Sold Out';

      if (stickyBuyBtn) {
        stickyBuyBtn.disabled    = !v.available;
        stickyBuyBtn.textContent = v.available ? 'Buy Now' : 'Sold Out';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyBar);
  } else {
    initStickyBar();
  }

})();